import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BarChart3, Loader2, Save } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

export const DEFAULT_GOOGLE_ANALYTICS_CONFIG = {
  enabled: false,
  measurement_id: '',
};

const MEASUREMENT_ID_RE = /^G-[A-Z0-9]+$/i;

export function mergeGoogleAnalyticsFromApi(data) {
  const raw = data || {};
  return {
    enabled: Boolean(raw.enabled),
    measurement_id: String(raw.measurement_id || '').trim().toUpperCase(),
  };
}

const AdminGoogleAnalyticsTab = ({ apiUrl, token, config, onChange, onSaved }) => {
  const [saving, setSaving] = useState(false);

  const setField = (key, value) => {
    onChange({ ...config, [key]: value });
  };

  const handleSave = async () => {
    const payload = mergeGoogleAnalyticsFromApi(config);
    if (payload.enabled && !MEASUREMENT_ID_RE.test(payload.measurement_id)) {
      toast.error('ID de mesure GA4 requis (format G-XXXXXXXXXX) lorsque Google Analytics est activé.');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${apiUrl}/api/admin/google-analytics-config`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onChange(payload);
      onSaved?.(payload);
      toast.success('Configuration Google Analytics enregistrée');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === 'string' ? detail : 'Échec de l’enregistrement Google Analytics'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-[#1A2332] border-[#2D3748]">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-[#FF6B35]" />
          Google Analytics (GA4)
        </CardTitle>
        <CardDescription className="text-gray-400">
          Suivi des visites via Google Analytics 4 (respect du consentement cookies analytics).
          Configuration stockée en base — pas dans les variables d’environnement du build.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(e) => setField('enabled', e.target.checked)}
            className="rounded border-[#2D3748] bg-[#0B1120] text-blue-600 focus:ring-blue-500"
          />
          <span className="text-white font-medium">Activer Google Analytics sur le site public</span>
        </label>

        <div className={`space-y-4 ${config.enabled ? '' : 'opacity-60'}`}>
          <div className="space-y-2">
            <Label htmlFor="ga-measurement-id" className="text-white">
              ID de mesure (Measurement ID)
            </Label>
            <Input
              id="ga-measurement-id"
              type="text"
              placeholder="G-XXXXXXXXXX"
              value={config.measurement_id}
              onChange={(e) => setField('measurement_id', e.target.value.toUpperCase())}
              disabled={!config.enabled}
              className="bg-[#0B1120] border-[#2D3748] text-white placeholder:text-gray-500 disabled:opacity-50 font-mono"
            />
            <p className="text-xs text-gray-400">
              Admin Google Analytics → Flux de données → Détails du flux web → ID de mesure.
            </p>
          </div>
        </div>

        {config.enabled ? (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-sm text-gray-300 space-y-1">
            <p>
              <strong className="text-blue-300">Dev / prod :</strong> chaque environnement a sa propre base
              Mongo ; utilisez un flux GA4 distinct par environnement si besoin.
            </p>
            <p>
              Le script ne se charge qu’après acceptation des cookies « analytics » par le visiteur.
            </p>
            <p>
              Les pages vues sont envoyées automatiquement à chaque navigation (SPA).
            </p>
          </div>
        ) : null}

        <Button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Enregistrement…
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Enregistrer Google Analytics
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default AdminGoogleAnalyticsTab;
